import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  CheckIcon,
} from "@heroicons/react/20/solid";
import { proveedorDe, precioDe, unidadDe, esDescuento, esRenta } from "../../utils/catalogo";

const ACCENT = "#1b4d2e";

// Elegir qué se cobra. Era una lista plana de TODOS los servicios del venue
// con puro nombre: con 15 renglones ya costaba, con 60 es inservible —no hay
// forma de encontrar nada ni de saber cuál de dos parecidos es el bueno.
//
// CÓMO SE AGRUPA, y por qué así: los servicios NO tienen categoría propia.
// La única clasificación que existe en el modelo es de quién viene (FRA-347:
// o lo pone el venue, o lo trae un proveedor), y los proveedores SÍ traen
// `categoria`/`subcategoria` del catálogo compartido. Así que la jerarquía
// sale de ahí: Categoría → Proveedor → sus servicios, más un grupo propio
// para lo del venue. Sin el catálogo de categorías a la mano, degrada a
// agrupar por proveedor, que sigue siendo mejor que una lista plana.
//
// Si algún día los servicios llevan categoría propia, este componente es el
// único lugar que hay que tocar: `gruposDeConceptos` es puro.

const DEL_VENUE = "__venue__";
const RENTAS = "__rentas__";

// Acentos fuera y minúsculas: buscar "ambulancia" tiene que encontrar
// "Ambulancía" y "AMBULANCIA". Mismo criterio que utils/leadHelpers.
const normaliza = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const fmtPrecio = (n) => {
  const v = Number(n) || 0;
  return `$${v.toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;
};

/**
 * Arma los grupos del menú. Puro y exportado para poder probarlo.
 *
 * @param conceptos servicios cotizables
 * @param proveedores directorio del venue (opcional: sin él se agrupa por el
 *        `proveedorNombre` que el propio servicio guarda)
 * @param categoriaNombrePorId etiquetas del catálogo compartido (opcional)
 */
export function gruposDeConceptos(
  conceptos = [],
  proveedores = [],
  categoriaNombrePorId = {}
) {
  const proveedorPorId = {};
  (proveedores || []).forEach((p) => {
    if (p?.id) proveedorPorId[p.id] = p;
  });

  // clave de grupo → { label, orden, subgrupos: Map(label → items) }
  const grupos = new Map();
  const grupo = (clave, label, orden) => {
    if (!grupos.has(clave)) {
      grupos.set(clave, { clave, label, orden, subgrupos: new Map() });
    }
    return grupos.get(clave);
  };
  const push = (g, subLabel, item) => {
    if (!g.subgrupos.has(subLabel)) g.subgrupos.set(subLabel, []);
    g.subgrupos.get(subLabel).push(item);
  };

  (conceptos || []).forEach((c) => {
    if (!c?.id) return;
    // Las rentas hasta arriba y en su propio grupo: son lo primero que se
    // cotiza y un evento de varios días lleva varias, así que mezclarlas con
    // el resto de lo del venue obligaba a buscarlas entre 40 renglones.
    if (esRenta(c)) {
      push(grupo(RENTAS, "Rentas", -1), "", c);
      return;
    }
    const pid = proveedorDe(c);
    if (!pid) {
      push(grupo(DEL_VENUE, "Del venue", 0), "", c);
      return;
    }
    const prov = proveedorPorId[pid];
    const provNombre = prov?.nombre || c.proveedorNombre || "Proveedor";
    const catId = prov?.categoria || null;
    // Sin etiqueta en el catálogo, la categoría NO existe para efectos del
    // menú: caer al id crudo pintaría un encabezado "cat-seg". Se degrada a
    // agrupar por proveedor, que es legible siempre.
    const catNombre = catId ? categoriaNombrePorId[catId] || null : null;
    if (catNombre) {
      push(grupo(catId, catNombre, 1), provNombre, c);
    } else {
      // Sin categoría (o sin catálogo cargado): el proveedor ES el grupo.
      push(grupo(`prov:${pid}`, provNombre, 2), "", c);
    }
  });

  const ordenaItems = (items) =>
    [...items].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es"));

  return [...grupos.values()]
    .sort(
      (a, b) =>
        a.orden - b.orden || a.label.localeCompare(b.label, "es")
    )
    .map((g) => ({
      clave: g.clave,
      label: g.label,
      subgrupos: [...g.subgrupos.entries()]
        .sort(([a], [b]) => a.localeCompare(b, "es"))
        .map(([label, items]) => ({ label, items: ordenaItems(items) })),
    }));
}

/** Filtra los grupos por texto, quitando los que se quedan vacíos. */
export function filtraGrupos(grupos, termino) {
  const q = normaliza(termino).trim();
  if (!q) return grupos;
  return grupos
    .map((g) => {
      // Si el término pega con el nombre del grupo (la categoría) o del
      // subgrupo (el proveedor), se conserva entero: buscar "ambulancias
      // vital" tiene que traer todo lo suyo, no solo lo que se llame así.
      const grupoPega = normaliza(g.label).includes(q);
      const subgrupos = g.subgrupos
        .map((sg) => {
          if (grupoPega || normaliza(sg.label).includes(q)) return sg;
          const items = sg.items.filter((c) =>
            normaliza(c.nombre).includes(q)
          );
          return items.length ? { ...sg, items } : null;
        })
        .filter(Boolean);
      return subgrupos.length ? { ...g, subgrupos } : null;
    })
    .filter(Boolean);
}

export default function ConceptoPicker({
  conceptos = [],
  proveedores = [],
  categoriaNombrePorId = {},
  value,
  onChange,
  opcionesExtra = [],
  placeholder = "Seleccionar…",
  disabled = false,
}) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  // Hacia arriba cuando abajo no cabe. En la cotización del lead el panel
  // caía justo sobre la barra fija de "Guardar cambios" y se comía media
  // lista; en un evento largo pasa lo mismo contra el borde de la ventana.
  const [haciaArriba, setHaciaArriba] = useState(false);
  const contenedor = useRef(null);
  const boton = useRef(null);
  const buscador = useRef(null);

  // Alto del panel (max-h-72 = 18rem) más el buscador y un respiro para la
  // barra fija de abajo.
  const ALTO_PANEL = 288 + 52 + 88;

  const grupos = useMemo(
    () => gruposDeConceptos(conceptos, proveedores, categoriaNombrePorId),
    [conceptos, proveedores, categoriaNombrePorId]
  );
  const visibles = useMemo(() => filtraGrupos(grupos, busqueda), [grupos, busqueda]);

  const elegido = useMemo(() => {
    const extra = opcionesExtra.find((o) => o.value === value);
    if (extra) return extra.label;
    const c = (conceptos || []).find((x) => x.id === value);
    return c ? c.nombre : "";
  }, [value, conceptos, opcionesExtra]);

  // Cerrar al hacer clic fuera o con Escape: el panel tapa el formulario.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e) => {
      if (contenedor.current && !contenedor.current.contains(e.target)) {
        setAbierto(false);
      }
    };
    const esc = (e) => e.key === "Escape" && setAbierto(false);
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", esc);
    };
  }, [abierto]);

  useEffect(() => {
    if (abierto) {
      setBusqueda("");
      // El foco va al buscador: abrir y escribir tiene que ser un solo gesto.
      const t = setTimeout(() => buscador.current?.focus(), 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [abierto]);

  const alternar = () => {
    if (!abierto && boton.current) {
      const r = boton.current.getBoundingClientRect();
      setHaciaArriba(
        window.innerHeight - r.bottom < ALTO_PANEL && r.top > ALTO_PANEL
      );
    }
    setAbierto((v) => !v);
  };

  const elegir = (val) => {
    onChange?.(val);
    setAbierto(false);
  };

  const hayAlgo = visibles.length > 0;

  return (
    <div className="relative" ref={contenedor}>
      <button
        ref={boton}
        type="button"
        disabled={disabled}
        onClick={alternar}
        className="w-full flex items-center gap-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-left text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e] disabled:opacity-50"
      >
        <span className={`flex-1 min-w-0 truncate ${elegido ? "" : "text-zinc-400"}`}>
          {elegido || placeholder}
        </span>
        <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-zinc-400" />
      </button>

      {abierto && (
        <div
          className={`absolute z-[45] w-full rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg ${
            haciaArriba ? "bottom-full mb-1" : "mt-1"
          }`}
        >
          <div className="relative border-b border-zinc-100 dark:border-zinc-700/60 p-2">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              ref={buscador}
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar servicio, proveedor o categoría…"
              className="w-full rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            />
          </div>

          {/* Sin padding vertical: `top-0` de un sticky se ancla al borde del
              PADDING del contenedor que scrollea, así que un `py-1` deja una
              franja de 4px por la que se asoman los renglones al pasar detrás
              del encabezado. El aire va dentro, no aquí. */}
          <div className="max-h-72 overflow-y-auto pb-1">
            {!hayAlgo && (
              <div className="px-3 py-4 text-center text-sm text-zinc-400">
                Nada coincide con “{busqueda}”.
              </div>
            )}

            {visibles.map((g) => (
              <div key={g.clave}>
                {/* Fondo OPACO a propósito: el verde de marca con alpha
                    (ACCENT + "1a") dejaría ver los renglones pasando por
                    detrás, que era el problema. Estos son el mismo tono ya
                    mezclado contra el fondo de cada tema. */}
                <div className="sticky top-0 z-10 border-b border-zinc-200/70 bg-[#e8edea] px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-[#1b4d2e] dark:border-zinc-700/60 dark:bg-[#192520] dark:text-emerald-200">
                  {g.label}
                </div>
                {g.subgrupos.map((sg) => (
                  <div key={`${g.clave}:${sg.label}`}>
                    {sg.label && (
                      <div className="bg-white px-3 pt-1.5 pb-0.5 text-xs font-medium text-zinc-500 dark:bg-zinc-900">
                        {sg.label}
                      </div>
                    )}
                    {sg.items.map((c) => {
                      const activo = c.id === value;
                      const unidad = unidadDe(c);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => elegir(c.id)}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition ${
                            sg.label ? "pl-6" : ""
                          } ${
                            activo
                              ? "bg-zinc-100 dark:bg-zinc-800"
                              : "hover:bg-zinc-50 dark:hover:bg-zinc-800/60"
                          }`}
                        >
                          <span className="flex-1 min-w-0">
                            <span className="block truncate text-zinc-900 dark:text-zinc-100">
                              {c.nombre}
                              {esDescuento(c) && (
                                <span className="ml-1.5 text-xs text-zinc-400">
                                  Descuento
                                </span>
                              )}
                            </span>
                            {/* El precio en la lista evita el "¿cuál de los
                                dos era el bueno?" sin tener que elegir uno
                                para averiguarlo. */}
                            <span className="block truncate text-xs text-zinc-400">
                              {fmtPrecio(precioDe(c))}
                              {unidad ? ` · por ${unidad}` : ""}
                            </span>
                          </span>
                          {activo && (
                            <CheckIcon
                              className="h-4 w-4 shrink-0"
                              style={{ color: ACCENT }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}

            {opcionesExtra.length > 0 && (
              <div className="mt-1 border-t border-zinc-100 dark:border-zinc-700/60 pt-1">
                {opcionesExtra.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => elegir(o.value)}
                    className="w-full px-3 py-1.5 text-left text-sm font-medium transition hover:bg-zinc-50 dark:hover:bg-zinc-800/60"
                    style={{ color: ACCENT }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
